import { useCallback, useContext, useEffect, useMemo } from 'react';
import AnimateHeight from 'react-animate-height';
import AppliedFiltersContext, {
  type ModelsKey
} from '~/context/srp/AppliedFiltersContext';
import { useFacetData } from '~/context/srp/FacetDataContext';
import type {
  AppliedFilters,
  CommonModelFacet,
  MakesFacet,
  MakeType,
  TrimFacet
} from '~/contracts/srp';
import Checkbox from './Checkbox';
import FilterDrawer from './FilterDrawer';
import Label from './Label';
import { CommonWrapper, TrimWrapper } from './styles';
import useExpand from './useExpand';
import WithClassName from './WithClassName';

type SelectedModel = {
  make: string;
  model: string;
  modelsKey: ModelsKey;
  trims?: Array<TrimFacet>;
};

function useAppliedMakes(appliedFilters: AppliedFilters): MakeType[] {
  return useMemo(
    () =>
      (appliedFilters.makes ?? []).sort((a, b) => a.name.localeCompare(b.name)),
    [appliedFilters.makes]
  );
}

function useFacetDataForSelectedModels(
  appliedMakes: Array<MakeType>,
  makes: MakesFacet = {}
): SelectedModel[] {
  return useMemo(
    () =>
      appliedMakes.flatMap(appliedMake => {
        if (!(appliedMake.name in makes)) return [];

        const make = makes[appliedMake.name];
        const modelsKey: ModelsKey = appliedMake.models
          ? 'models'
          : 'parentModels';
        const appliedModels = appliedMake[modelsKey] ?? [];
        const modelsToCheck: CommonModelFacet[] = appliedMake.models
          ? make.models ?? []
          : make.parentModels ?? [];

        return appliedModels
          ? appliedModels
              .flatMap(appliedModel => {
                const model = modelsToCheck.find(
                  m => m.key === appliedModel.name
                );

                return model
                  ? {
                      make: make.key,
                      model: model.key,
                      modelsKey,
                      trims: model.trims
                    }
                  : [];
              })
              .sort((a, b) => a.model.localeCompare(b.model))
          : [];
      }),
    [appliedMakes, makes]
  );
}

function getIsSelected(
  makes: MakeType[],
  makeName: string,
  modelsKey: ModelsKey,
  modelName: string,
  trim: string
): boolean {
  const make = makes.find(m => m.name === makeName);
  const model = make?.[modelsKey]?.find(m => m.name === modelName);

  return model?.trims?.includes(trim) ?? false;
}

export default function Trim({ className }: WithClassName) {
  const [expand, setExpand] = useExpand(false);
  const { appliedFilters, updateMakes } = useContext(AppliedFiltersContext);
  const facetData = useFacetData();

  const appliedMakes = useAppliedMakes(appliedFilters);
  const selectedModels = useFacetDataForSelectedModels(
    appliedMakes,
    facetData.makes
  );

  const includeMakeInDisplay = appliedMakes.length > 1;
  const show = selectedModels.length > 0;

  useEffect(() => {
    if (!show) setExpand(false);
  }, [setExpand, show]);

  return (
    <AnimateHeight duration={500} height={show ? 'auto' : 0}>
      <FilterDrawer
        className={className}
        dataQa="facet-trims"
        facetContent={appliedMakes}
        facetName="Trim"
        show={expand}
        onToggle={useCallback(() => setExpand(v => !v), [setExpand])}
      >
        <CommonWrapper>
          {selectedModels.map(({ make, model, modelsKey, trims }) => (
            <TrimWrapper key={`${model}-trims`}>
              <h4 className="tl-header-s">
                {includeMakeInDisplay ? make : ''} {model}
              </h4>
              {trims?.map(({ key: trim }) => (
                <Checkbox
                  checked={getIsSelected(
                    appliedMakes,
                    make,
                    modelsKey,
                    model,
                    trim
                  )}
                  className="pl-4"
                  facet="trims"
                  filter={trim}
                  id={`trim-${model}-${trim}`}
                  key={trim}
                  onChange={() =>
                    updateMakes({
                      modelsKey,
                      name: trim,
                      parents: [make, model]
                    })
                  }
                  label={<Label displayName={trim} />}
                />
              ))}
            </TrimWrapper>
          ))}
        </CommonWrapper>
      </FilterDrawer>
    </AnimateHeight>
  );
}
